import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import arraySort from 'array-sort';
import { GetInvoicesHeaders, GetInvoicesHeadersCount, CopyInvoice, DeleteInvoice, DeleteInvoices,
  UpdateInvoiceLeftToPayAmount,DeleteCorrectionInvoice, DeleteCorrectionInvoices, UpdateCorrectionInvoiceLeftToPayAmount} from '../../engine/baseApiClient';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

const initialState = {
  listDirtyFlag: true,
  invoices: [],
  pagination: {
    totalInvoices: 0,
    rowsPerPageOptions: [25],
    pageSize: 25,
    page: 1,
  },
  filters: {
    fromDate: dayjs().utc(true).startOf('month'),
    toDate: dayjs().utc(true).endOf('month')
  },
  ordering: ['series', 'number'],
  error:false,
  errorMessage:''
};

export const doGetInvoicesAsync = createAsyncThunk(
  "invoices/doGetInvoicesAsync",
  async ({filters, pagination}) => {
    const response = await GetInvoicesHeaders(filters.fromDate, filters.toDate, pagination.pageSize, pagination.pageSize * (pagination.page - 1));
    const totalCount = await GetInvoicesHeadersCount(filters.fromDate, filters.toDate);
    return { response, totalCount };
  }
);

export const doCopyInvoiceAsync = createAsyncThunk(
  "invoices/doCopyInvoiceAsync",
  async (payload) => {
    const copyInvoiceResponse = await CopyInvoice(payload);   
    if(copyInvoiceResponse.status == 500){     
      throw new Error(copyInvoiceResponse.detail);
    }
    return copyInvoiceResponse;
  }
);

export const doUpdateInvoiceLeftToPayAmountAsync = createAsyncThunk(
  "invoices/doUpdateInvoiceLeftToPayAmountAsync",
  async(payload) => {
    const response = await UpdateInvoiceLeftToPayAmount(payload);
    return response;
  }
);

export const doUpdateCorrectionInvoiceLeftToPayAmountAsync = createAsyncThunk(
  "invoices/doUpdateCorrectionInvoiceLeftToPayAmountAsync",
  async(payload) => {
    const response = await UpdateCorrectionInvoiceLeftToPayAmount(payload);
    return response;
  }
);

export const doDeleteInvoiceAsync = createAsyncThunk(
  "invoices/doDeleteInvoiceAsync",
  async (id) => {
    const response = await DeleteInvoice(id);

    if(response.status == 409){
      throw new Error(response.detail);
    }

    return response;
  }
);

export const doDeleteCorrectionInvoiceAsync = createAsyncThunk(
  "correctionInvoiceEditor/doDeleteCorrectionInvoiceAsync",
  async (payload) => {   
      const response = await DeleteCorrectionInvoice(payload);
      return response;
  }
);

export const doDeleteInvoicesAsync = createAsyncThunk(
  "invoices/doDeleteInvoicesAsync",
  async (ids) => {
    const response = await DeleteInvoices(ids);

    if(response.status == 409){
      throw new Error(response.detail);
    }

    return response;
  }
);

export const doDeleteCorrectionInvoicesAsync = createAsyncThunk(
  "correctionInvoiceEditor/doDeleteCorrectionInvoicesAsync",
  async (ids) => {   
      const response = await DeleteCorrectionInvoices(ids);
      return response;
  }
);

const toDataGridRow = (data) => {
  return { ...data };
};

export const invoicesSlice = createSlice({
  name: 'invoices',
  initialState,
  reducers: {
    doUpdateFilters: (state, action) => {
      switch (action.payload.type) {
        case 'fromDate': { state.filters.fromDate = action.payload.value; return; }
        case 'toDate': { state.filters.toDate = action.payload.value; return; }
        case 'filters': { state.filters = action.payload.value; return; }
      }
    },
    doUpdateOrdering: (state, action) => {
      state.ordering = action.payload;
    },
    doUpdateError: (state, action) => {
      state.error = action.payload;
    },
    doSetPage: (state, action) => {
      state.pagination.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(doGetInvoicesAsync.fulfilled, (state, action) => {
        state.invoices = arraySort(
          action.payload.response.map(invoice => toDataGridRow(invoice)),
          state.ordering
        );
        state.listDirtyFlag = false;
        state.pagination.totalInvoices = action.payload.totalCount;
      })
      .addCase(doCopyInvoiceAsync.fulfilled, (state, action) => {
        state.listDirtyFlag = true;
      })
      .addCase(doCopyInvoiceAsync.rejected, (state, action) => {
        state.error = true;
        state.errorMessage = action.error.message;
      })
      .addCase(doDeleteInvoiceAsync.fulfilled, (state, action) => {
        state.listDirtyFlag = true;
      })
      .addCase(doDeleteInvoiceAsync.rejected, (state, action) => {
        state.error = true;
        state.errorMessage = action.error.message;
      })
      .addCase(doDeleteCorrectionInvoiceAsync.fulfilled, (state, action) => {
        state.listDirtyFlag = true;
      })
      .addCase(doDeleteInvoicesAsync.fulfilled, (state, action) => {
        state.listDirtyFlag = true;
      })
      .addCase(doDeleteInvoicesAsync.rejected, (state, action) => {
        state.error = true;
        state.errorMessage = action.error.message;
      })
      .addCase(doDeleteCorrectionInvoicesAsync.fulfilled, (state, action) => {
        state.listDirtyFlag = true;
      })      
      .addCase(doUpdateInvoiceLeftToPayAmountAsync.fulfilled, (state, action) => {
        state.listDirtyFlag = true;
      })
      .addCase(doUpdateCorrectionInvoiceLeftToPayAmountAsync.fulfilled, (state, action) => {
        state.listDirtyFlag = true;
      })
  }
});

export const selectInvoices = (state) => state.invoices.invoices;
export const selectPagination = (state) => state.invoices.pagination;
export const selectFilters = (state) => state.invoices.filters;
export const selectOrdering = (state) => state.invoices.ordering;
export const selectListDirtyFlag = (state) => state.invoices.listDirtyFlag;
export const selectError = (state) => state.invoices.error;
export const selectErrorMessage = (state) => state.invoices.errorMessage;

export const { doUpdateFilters, doUpdateOrdering, doUpdateError, doSetPage } = invoicesSlice.actions;
export default invoicesSlice.reducer;
